/* eslint-disable max-lines */

import { FunctionComponent, useEffect, useState, type ReactNode } from 'react';
import { levelCanUpgradeTo, Levels, QueryParams } from '../../core/enums/module';
import { Transaction } from '../../core/domain/module';
import { Subscriptions } from '../../core/services/module';
import { SubscriptionProductFamilies } from '../../core/services/subscriptions/subscriptions';
import SubmitButton from '../submitButton/SubmitButton';
import { Icons } from '../../icons';
import uppercaseFirst from '../../utilities/uppercaseFirst';
import { executeWithinBoundary } from '../../utilities/executeWithinBoundary';
import { getQueryParameter } from '../../utilities/getQueryParameter';
import { TransactionFieldProps } from './TransactionField';
import styles from './LevelSelectionChart.module.scss';
import { shouldUseCabelasBranding } from '../../utilities/shouldUseCabelasBranding';
import { getVariantType } from '../../utilities/getVariantType';
import { VariantType } from '../../utilities/typeGuards/isVariantType';
import { LevelSelectionChartData } from './LevelSelectionChartData';
import { Brands } from '@delta-defense/client-environments';

const levelRank: Record<Levels, number> = {
	[Levels.Gold]: 1,
	[Levels.Platinum]: 2,
	[Levels.Elite]: 3
};

const orderedLevels = [Levels.Gold, Levels.Platinum, Levels.Elite];

export const LevelSelectionChart: FunctionComponent<TransactionFieldProps> = ({
	field,
	className,
	SubmitButtonComponent = SubmitButton,
	transaction = Transaction.Instance(),
	subscriptions = Subscriptions.Instance()
}) => {
	const [productFamilies, setProductFamilies] = useState<SubscriptionProductFamilies | undefined>();

	useEffect(() => {
		executeWithinBoundary(async () => {
			const productsResult = await subscriptions.getMembershipProducts(getQueryParameter(QueryParams.ProductId) || undefined);
			if (productsResult.wasSuccessful && productsResult.value) {
				setProductFamilies(productsResult.value);
			}
		});
	}, []);

	const getButtonText = (level: Levels) => {
		const currentLevel = transaction.UpgradingFromLevel;
		const canUpgradeToLevel = levelCanUpgradeTo(transaction.UpgradingFromLevel, level);

		if (!currentLevel) {
			return `Join ${uppercaseFirst(level)} Now`;
		} else if ((currentLevel.toLowerCase() as Levels) === level) {
			return 'Current Level';
		} else {
			return canUpgradeToLevel ? 'Upgrade' : 'not available';
		}
	};

	const getCurrentMembershipLevel = () => {
		const currentLevel = transaction.PrimarySub?.attributes.level || transaction.UpgradingFromLevel;
		const normalizedLevel = currentLevel?.toLowerCase() as Levels;

		return Object.values(Levels).includes(normalizedLevel) ? normalizedLevel : undefined;
	};

	const shouldShowLevel = (level: Levels) => {
		const currentLevel = getCurrentMembershipLevel();
		return !currentLevel || levelRank[level] > levelRank[currentLevel];
	};

	const renderLevel = (level: Levels, content: ReactNode) => (shouldShowLevel(level) ? content : null);
	const visibleLevels = orderedLevels.filter(shouldShowLevel);
	const shouldShowDesktopBorder = (level: Levels) => visibleLevels.indexOf(level) > 0;

	const getDesktopLevelClassName = (level: Levels, levelClassName: string) => {
		return `${styles.levelInfo} ${levelClassName} ${shouldShowDesktopBorder(level) ? styles.border : ''}`;
	};

	const clickHandlerFactory = (level: Levels) => () => {
		field.SetValue(level);
	};

	const getFallbackLevelCost = (level: Levels, by_month: boolean) => {
		const defaultLevelCostByLevelType: Record<Levels, number> = {
			[Levels.Gold]: by_month ? 39 : 399,
			[Levels.Platinum]: by_month ? 49 : 499,
			[Levels.Elite]: by_month ? 59 : 599
		};

		const iiaLevelCostByLevelType: Record<Levels, number> = {
			[Levels.Gold]: by_month ? 37 : 379,
			[Levels.Platinum]: by_month ? 47 : 474,
			[Levels.Elite]: by_month ? 56 : 569
		};

		const levelCostByVariantType: Record<VariantType, Record<Levels, number>> = {
			[VariantType.Default]: defaultLevelCostByLevelType,
			[VariantType.Cabelas]: defaultLevelCostByLevelType,
			[VariantType.IIA]: iiaLevelCostByLevelType,
			[VariantType.Chapter]: defaultLevelCostByLevelType
		};

		return levelCostByVariantType[getVariantType()][level];
	};

	const getLevelCost = (level: Levels, by_month = true): number => {
		const family = productFamilies?.[level];
		const productCost = by_month ? family?.monthly.price : family?.annual.price;
		return productCost ?? getFallbackLevelCost(level, by_month);
	};

	const formatPrice = (price: number) => (Number.isInteger(price) ? `${price}` : price.toFixed(2));

	const getLevelPricing = (level: Levels) => {
		const byMonth = getLevelCost(level, true);
		const byYear = getLevelCost(level, false);
		return {
			byMonth: formatPrice(byMonth),
			byYear: formatPrice(byYear),
			savings: formatPrice(Math.round((byMonth * 12 - byYear) * 100) / 100)
		};
	};

	const useCabelasBranding = shouldUseCabelasBranding();
	const activeBrand = useCabelasBranding ? Brands.CabelasShield : Brands.USCCA;

	const goldPricing = getLevelPricing(Levels.Gold);
	const platPricing = getLevelPricing(Levels.Platinum);
	const elitePricing = getLevelPricing(Levels.Elite);

	return (
		<div className={`${className ? className : ''} ${styles.wrapper}`}>
			<div className={`${styles.mobile}`}>
				<h2>
					In addition to the benefits
					<br /> listed above, you receive:{' '}
				</h2>

				{renderLevel(
					Levels.Gold,
					<div className={`${styles.levelInfo} ${styles.goldLevel}`}>
						<div>
							<div className={`${styles.price} ${styles.gold}`}>
								{useCabelasBranding ? (
									<div className={`${styles.shield} ${styles.shield_gold}`}>{Icons.ChartShield}</div>
								) : (
									Icons.MembershipShield
								)}
								<h2>
									<span>Gold Membership</span> <br />
									<span>${goldPricing.byMonth}/month</span>
									<span>
										<i>
											{' '}
											or ${goldPricing.byYear}/yr (SAVE ${goldPricing.savings})
										</i>
									</span>
								</h2>
							</div>
							<div>
								<ul>
									{LevelSelectionChartData.getBenefitDetails({
										brand: activeBrand,
										level: Levels.Gold,
										isMobile: true
									})}
								</ul>
							</div>
						</div>
						<div className={`${styles.marginAuto}`}>
							<SubmitButtonComponent
								disableStickyBottomFeatureFlag
								className={styles.button_gold}
								idSuffix={`mobile_${Levels.Gold}`}
								onClick={clickHandlerFactory(Levels.Gold)}
							>
								{getButtonText(Levels.Gold)}
							</SubmitButtonComponent>
							<p className={`${styles.cancelText}`}>
								<i>cancel anytime</i>
							</p>
						</div>
					</div>
				)}

				{renderLevel(
					Levels.Platinum,
					<div className={`${styles.levelInfo} ${styles.platinumLevel}`}>
						<div>
							<div className={`${styles.price} ${styles.platinum}`}>
								{useCabelasBranding ? (
									<div className={`${styles.shield} ${styles.shield_platinum}`}>{Icons.ChartShield}</div>
								) : (
									Icons.MembershipShield
								)}
								<h2>
									<span>Platinum Membership</span> <br />
									<span>${platPricing.byMonth}/month</span>
									<span>
										<i>
											{' '}
											or ${platPricing.byYear}/yr (SAVE ${platPricing.savings})
										</i>
									</span>
								</h2>
							</div>
							<div>
								<ul>
									{LevelSelectionChartData.getBenefitDetails({
										brand: activeBrand,
										level: Levels.Platinum,
										isMobile: true
									})}
								</ul>
							</div>
						</div>
						<div className={`${styles.marginAuto}`}>
							<p className={`${styles.mostPopular}`}>Most Popular</p>
							<SubmitButtonComponent
								disableStickyBottomFeatureFlag
								className={styles.button_platinum}
								idSuffix={`mobile_${Levels.Platinum}`}
								onClick={clickHandlerFactory(Levels.Platinum)}
							>
								{getButtonText(Levels.Platinum)}
							</SubmitButtonComponent>
							<p className={`${styles.cancelText}`}>
								<i>cancel anytime</i>
							</p>
						</div>
					</div>
				)}

				{renderLevel(
					Levels.Elite,
					<div className={`${styles.levelInfo} ${styles.eliteLevel}`}>
						<div>
							<div className={`${styles.price} ${styles.elite}`}>
								{useCabelasBranding ? (
									<div className={`${styles.shield} ${styles.shield_elite}`}>{Icons.ChartShield}</div>
								) : (
									Icons.MembershipShield
								)}
								<h2>
									<span>Elite Membership</span> <br />
									<span>${elitePricing.byMonth}/month</span>
									<span>
										<i>
											{' '}
											or ${elitePricing.byYear}/yr (SAVE ${elitePricing.savings})
										</i>
									</span>
								</h2>
							</div>
							<div>
								<ul>
									{LevelSelectionChartData.getBenefitDetails({
										brand: activeBrand,
										level: Levels.Elite,
										isMobile: true
									})}
								</ul>
							</div>
						</div>
						<div className={`${styles.marginAuto}`}>
							<SubmitButtonComponent
								disableStickyBottomFeatureFlag
								className={styles.button_elite}
								idSuffix={`mobile_${Levels.Elite}`}
								onClick={clickHandlerFactory(Levels.Elite)}
							>
								{getButtonText(Levels.Elite)}
							</SubmitButtonComponent>
							<p className={`${styles.cancelText}`}>
								<i>cancel anytime</i>
							</p>
						</div>
					</div>
				)}
			</div>

			<div className={`${styles.desktop}`}>
				{renderLevel(
					Levels.Gold,
					<div className={getDesktopLevelClassName(Levels.Gold, styles.goldLevel)}>
						<div>
							{useCabelasBranding ? (
								<div className={`${styles.shield} ${styles.shield_gold}`}>{Icons.ChartShield}</div>
							) : (
								Icons.MembershipShield
							)}
							<h2>Gold Membership</h2>
							<div className={`${styles.priceBackground}`}>
								<p>${goldPricing.byMonth}/month</p>
								<p>
									<i>
										or ${goldPricing.byYear}/yr (SAVE ${goldPricing.savings})
									</i>
								</p>
							</div>
							<div>
								<ul>
									{LevelSelectionChartData.getBenefitDetails({
										brand: activeBrand,
										level: Levels.Gold
									})}
								</ul>
							</div>
						</div>
						<div>
							<SubmitButtonComponent
								disableStickyBottomFeatureFlag
								className={styles.button_gold}
								idSuffix={`desktop_${Levels.Gold}`}
								onClick={clickHandlerFactory(Levels.Gold)}
							>
								{getButtonText(Levels.Gold)}
							</SubmitButtonComponent>
							<p className={`${styles.cancelText}`}>
								<i>cancel anytime</i>
							</p>
						</div>
					</div>
				)}

				{renderLevel(
					Levels.Platinum,
					<div className={getDesktopLevelClassName(Levels.Platinum, styles.platinumLevel)}>
						<div>
							{useCabelasBranding ? (
								<div className={`${styles.shield} ${styles.shield_platinum}`}>{Icons.ChartShield}</div>
							) : (
								Icons.MembershipShield
							)}
							<h2>Platinum Membership</h2>
							<div className={`${styles.priceBackground}`}>
								<p>${platPricing.byMonth}/month</p>
								<p>
									<i>
										or ${platPricing.byYear}/yr (SAVE ${platPricing.savings})
									</i>
								</p>
							</div>
							<div>
								<ul>
									{LevelSelectionChartData.getBenefitDetails({
										brand: activeBrand,
										level: Levels.Platinum
									})}
								</ul>
							</div>
						</div>
						<div>
							<p className={`${styles.mostPopular}`}>Most Popular</p>
							<SubmitButtonComponent
								disableStickyBottomFeatureFlag
								className={styles.button_platinum}
								idSuffix={`desktop_${Levels.Platinum}`}
								onClick={clickHandlerFactory(Levels.Platinum)}
							>
								{getButtonText(Levels.Platinum)}
							</SubmitButtonComponent>
							<p className={`${styles.cancelText}`}>
								<i>cancel anytime</i>
							</p>
						</div>
					</div>
				)}

				{renderLevel(
					Levels.Elite,
					<div className={getDesktopLevelClassName(Levels.Elite, styles.eliteLevel)}>
						<div>
							{useCabelasBranding ? (
								<div className={`${styles.shield} ${styles.shield_elite}`}>{Icons.ChartShield}</div>
							) : (
								Icons.MembershipShield
							)}
							<h2>Elite Membership</h2>
							<div className={`${styles.priceBackground}`}>
								<p>${elitePricing.byMonth}/month</p>
								<p>
									<i>
										or ${elitePricing.byYear}/yr (SAVE ${elitePricing.savings})
									</i>
								</p>
							</div>
							<div>
								<ul>
									{LevelSelectionChartData.getBenefitDetails({
										brand: activeBrand,
										level: Levels.Elite
									})}
								</ul>
							</div>
						</div>
						<div>
							<SubmitButtonComponent
								disableStickyBottomFeatureFlag
								className={styles.button_elite}
								idSuffix={`desktop_${Levels.Elite}`}
								onClick={clickHandlerFactory(Levels.Elite)}
							>
								{getButtonText(Levels.Elite)}
							</SubmitButtonComponent>
							<p className={`${styles.cancelText}`}>
								<i>cancel anytime</i>
							</p>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
